import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Location, PopStateEvent } from '@angular/common';
import { Directive, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import { Logger, LoggerFactory } from './../../core/logger.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

/*
  Scrolls to the top of the page if it's a new route

  The expected behavior is that when you navigate back to the page, it should remain scrolled
  down to the same location it was when you clicked on the link, but scrolling to the top when arriving at
  every page obviously breaks this expectation.

  If the page is shorter we need to keep track of the original y scroll position and restored
  it explicitly when you go back to the page.
*/
@Directive({
  selector: '[newRouteToTop]',
  standalone: false,
})
export class NewRouteToTopDirective implements OnInit, OnDestroy {
  private onDestroy: Subject<any> = new Subject();
  private lastPoppedUrl: string;
  private yScrollStack: number[] = [];
  private readonly logger: Logger;
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor(
    private readonly router: Router,
    private readonly location: Location,
    public readonly loggerFactory: LoggerFactory,
  ) {
    this.logger = loggerFactory.get('NewRouteToTopDirective');
  }

  public ngOnInit(): void {
    // Save the last url
    this.location.subscribe((ev: PopStateEvent) => {
      this.lastPoppedUrl = ev.url;
    });

    // Listen to router events
    this.router.events
      .pipe(takeUntil(this.onDestroy))
      .pipe(filter((e) => e instanceof NavigationStart || e instanceof NavigationEnd))
      .subscribe((e: any) => {
        if (e instanceof NavigationStart) {
          this.handleNavigationStart((<NavigationStart>e).url);
          return;
        }
        if (e instanceof NavigationEnd) {
          this.handleNavigationEnd((<NavigationEnd>e).url);
          return;
        }
        this.logger.error('Filtered event is being tried to handled');
      });
  }

  public ngOnDestroy(): void {
    if (this.onDestroy) {
      this.onDestroy.next(undefined);
      this.onDestroy.complete();
    }
  }

  private handleNavigationStart(newUrl: string): void {
    if (newUrl !== this.lastPoppedUrl) {
      this.yScrollStack.push(this.scrollTop());
    }
  }

  private handleNavigationEnd(newUrl: string): void {
    if (newUrl === this.lastPoppedUrl) {
      this.lastPoppedUrl = undefined;
      this.scrollTo(0, this.yScrollStack.pop());
    } else {
      this.scrollTo(0, 0);
    }
  }

  /**
   * The layout caps the drawer container at the viewport height, which makes Material's content
   * element the scroller rather than the window. Both are moved: pages that scroll the window
   * instead (a dialog host, a fullscreen view) are then covered by the same call.
   */
  private scrollTo(x: number, y: number) {
    window.scrollTo(x, y);
    this.scrollingContent()?.scrollTo({ left: x, top: y });
  }

  private scrollTop(): number {
    return this.scrollingContent()?.scrollTop ?? window.scrollY;
  }

  private scrollingContent(): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>('.mat-drawer-content');
  }
}
